import { Tag } from '@bp/ui-components';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { VersionLessonTableType } from '../components/Lessons/TimetableVersion/TimetableVersionLessonStore';
import { FilterLoaderArgs } from '../utils/routes-loader/filterLoader';
import { isFilterType } from '../utils/typeguards';
import { useLoadBasicData } from './useLoadBasicData';

export const useFilterTags = (versionUuid: string) => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const clearSearchParams = useCallback(() => {
    if (searchParams.has('filter')) {
      searchParams.delete('filter');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const { subjectData, teacherData, classesData, roomsData } = useLoadBasicData({ pause: false });

  useEffect(() => {
    clearSearchParams();
  }, [clearSearchParams]);

  const filterConfig = useLoaderData();
  const [_filterConfig, _setFilterConfig] = useState<FilterLoaderArgs | null>(null);

  useEffect(() => {
    if (filterConfig && isFilterType(filterConfig)) {
      _setFilterConfig(filterConfig);
    }
  }, [filterConfig]);

  const subject =
    _filterConfig && _filterConfig.column === 'subject'
      ? subjectData?.subjects.find((s) => s.uuid === _filterConfig?.filterValue)
      : null;

  const classes =
    _filterConfig && _filterConfig.column === 'classes'
      ? classesData?.classes.find((s) => s.uuid === _filterConfig?.filterValue)
      : null;

  const teacher =
    _filterConfig && _filterConfig.column === 'teachers'
      ? teacherData?.people.find((s) => s.uuid === _filterConfig?.filterValue)
      : null;

  const rooms =
    _filterConfig && _filterConfig.column === 'rooms'
      ? roomsData?.rooms.find((s) => s.uuid === _filterConfig?.filterValue)
      : null;

  const memoFilter = useCallback(
    (data: Pick<VersionLessonTableType, 'subjectUuid' | 'teachers' | 'classesUuids' | 'roomsUuids'>) => {
      if (_filterConfig && _filterConfig.column === 'subject') {
        return data.subjectUuid === _filterConfig.filterValue;
      }
      if (_filterConfig && _filterConfig.column === 'teachers') {
        return data.teachers.some((t) => t.uuid === _filterConfig.filterValue);
      }
      if (_filterConfig && _filterConfig.column === 'classes') {
        return data.classesUuids.some((c) => c === _filterConfig.filterValue);
      }
      if (_filterConfig && _filterConfig.column === 'rooms') {
        return data.roomsUuids?.some((c) => c === _filterConfig.filterValue);
      }
      return true;
    },
    [_filterConfig],
  );

  const FilterTag = (label: string, value?: string | null) => {
    if (!_filterConfig || !value) return null;
    return (
      <Tag
        closable
        size='small'
        value={`${label}: ${value}`}
        onClose={() => {
          clearSearchParams();
          _setFilterConfig(null);
        }}
      />
    );
  };

  return {
    memoFilter,
    filterTags: [
      FilterTag(t('subject.title', { count: 1 }), subject?.name),
      FilterTag(t('classes.title', { count: 1 }), classes?.name),
      FilterTag(t('persons.title', { count: 1, context: 'neutral' }), teacher?.selectName),
      FilterTag(t('rooms.title', { count: 1 }), rooms?.name),
    ],
  };
};
